import axios from "../../../utils/axios";
import {
  GetRateAlertsParams,
  RateAlertPayload,
  RateAlertRule,
  UpdateRateAlertPayload,
} from "./types";

// Live endpoints backing the Alerts tab:
//   GET    /admin/exchange-rates/alerts
//   POST   /admin/exchange-rates/alerts
//   PATCH  /admin/exchange-rates/alerts/{id}
//   PATCH  /admin/exchange-rates/alerts/activate/{id}
//   PATCH  /admin/exchange-rates/alerts/deactivate/{id}
//   DELETE /admin/exchange-rates/alerts/{id}
//
// The response shape for a rate-alert resource hasn't been confirmed with a
// real sample yet — `mapApiRateAlert` below is a best-effort guess (field
// names matching the given request-body schema, plus `is_active` for the
// boolean the activate/deactivate endpoints flip, falling back to `active`/
// `status` in case the real field name differs). Adjust it once real data is
// available. Mutating endpoints (create/update/activate/deactivate/delete)
// don't try to merge their response into local state for the same reason —
// callers re-fetch the current page after a successful mutation instead,
// so nothing here depends on guessing those response shapes correctly.
const ALERTS_ENDPOINT = "/admin/exchange-rates/alerts";

// The shared axios instance's response interceptor resolves instead of
// rejecting on most errors, so a failed request can land here as an
// AxiosError rather than throwing — this routes that case into the same
// try/catch handling as a thrown error instead of it being read as success.
function assertNotAxiosError(res: any) {
  if (res?.isAxiosError) throw res;
}

function mapApiRateAlert(raw: any): RateAlertRule {
  return {
    id: String(raw?.id),
    name: raw?.name ?? null,
    mode: raw?.mode,
    min_change_percent: Number(raw?.min_change_percent),
    currency_pairs: raw?.currency_pairs ?? null,
    competitors: raw?.competitors ?? null,
    is_active: raw?.is_active ?? raw?.active ?? raw?.status === "active",
  };
}

export async function fetchRateAlerts(params: GetRateAlertsParams = {}) {
  const res = await axios.get(ALERTS_ENDPOINT, {
    params: { page: params.page, per_page: params.per_page },
  });
  assertNotAxiosError(res);
  const rawItems = res?.data?.data || [];
  const rawMeta = res?.data?.meta;

  return {
    data: {
      status: res?.data?.status ?? true,
      message: res?.data?.message ?? "OK",
      data: {
        rules: rawItems.map(mapApiRateAlert),
        meta: rawMeta
          ? {
              current_page: rawMeta.current_page,
              per_page: rawMeta.per_page,
              total: rawMeta.total,
              last_page: rawMeta.last_page,
            }
          : undefined,
      },
    },
  };
}

export async function createRateAlert(payload: RateAlertPayload) {
  const res = await axios.post(ALERTS_ENDPOINT, {
    ...payload,
    currency_pairs: payload.currency_pairs?.length ? payload.currency_pairs : null,
    competitors: payload.competitors?.length ? payload.competitors : null,
  });
  assertNotAxiosError(res);

  return {
    data: {
      status: res?.data?.status ?? true,
      message: res?.data?.message ?? "Alert created",
    },
  };
}

export async function updateRateAlert(payload: UpdateRateAlertPayload) {
  const { id, ...body } = payload;
  const res = await axios.patch(`${ALERTS_ENDPOINT}/${id}`, {
    ...body,
    currency_pairs: body.currency_pairs?.length ? body.currency_pairs : null,
    competitors: body.competitors?.length ? body.competitors : null,
  });
  assertNotAxiosError(res);

  return {
    data: {
      status: res?.data?.status ?? true,
      message: res?.data?.message ?? "Alert updated",
    },
  };
}

export async function setRateAlertActive(id: string, active: boolean) {
  const res = await axios.patch(
    `${ALERTS_ENDPOINT}/${active ? "activate" : "deactivate"}/${id}`,
  );
  assertNotAxiosError(res);

  return {
    data: {
      status: res?.data?.status ?? true,
      message: res?.data?.message ?? (active ? "Alert activated" : "Alert deactivated"),
    },
  };
}

export async function deleteRateAlert(id: string) {
  const res = await axios.delete(`${ALERTS_ENDPOINT}/${id}`);
  assertNotAxiosError(res);

  return {
    data: {
      status: res?.data?.status ?? true,
      message: res?.data?.message ?? "Alert deleted",
    },
  };
}
